import React, { useState } from 'react'
import { Button } from '@heroui/react'
import { paisesBancarios, tiposCuenta } from './ConstantesEnlaces'

const RenderizarCuentaBancaria = ({ 
  enlace, 
  esEdicion = false, 
  onEdit, 
  onDelete, 
  showEditButtons = false,
  estiloMiniatura = false,
  estilosPerfil = {},
  copiarAlPortapapeles = () => {}
}) => {
  const [copiado, setCopiado] = useState(false)
  const { datosBancarios } = enlace
  
  if (!datosBancarios) return null
  
  const pais = paisesBancarios.find(p => p.codigo === datosBancarios.paisBanco)
  const tipoCuenta = tiposCuenta.find(t => t.id === datosBancarios.tipoCuenta)
  
  const manejarCopia = async (texto, tipo) => {
    try {
      await copiarAlPortapapeles(texto, `${tipo} copiado al portapapeles`)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    } catch (error) {
      console.error('Error al copiar:', error)
    }
  }

  return (
    <div className={`relative ${estiloMiniatura ? 'w-full' : ''} ${showEditButtons ? 'mt-2' : ''}`}>
      {/* Botones de edición - Solo mostrar si NO están siendo manejados por ContenedorEnlace */}
      {showEditButtons && (
        <div className="flex justify-end mb-2 pr-2">
          <div className="flex gap-1">
            <Button
              isIconOnly
              size="sm"
              color="primary"
              variant="solid"
              onPress={onEdit}
              className="shadow-lg min-w-8 h-8"
            >
              <i className="fas fa-edit text-xs"></i>
            </Button>
            <Button
              isIconOnly
              size="sm"
              color="danger"
              variant="solid"
              onPress={onDelete}
              className="shadow-lg min-w-8 h-8"
            >
              <i className="fas fa-trash text-xs"></i>
            </Button>
          </div>
        </div>
      )}
      
      {/* Contenido de la cuenta bancaria */}
      <div 
        className={`
          relative overflow-hidden rounded-xl 
          ${estiloMiniatura ? 'p-3' : 'p-6'} 
          shadow-lg transform transition-all duration-300 hover:scale-105
        `}
        style={{
          background: `linear-gradient(135deg, 
            ${estilosPerfil.primario || '#1e40af'} 0%, 
            ${estilosPerfil.secundario || '#3b82f6'} 100%
          )`,
          backgroundSize: '200% 200%',
          animation: 'gradient 3s ease infinite'
        }}
      >
        {/* Patrón de fondo decorativo */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, white 2px, transparent 2px),
                             radial-gradient(circle at 80% 50%, white 2px, transparent 2px)`,
            backgroundSize: '40px 40px'
          }}
        />
        
        {/* Contenido */}
        <div className="relative z-10 text-white">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className={`${estiloMiniatura ? 'text-sm' : 'text-lg'} font-bold`}>
                {enlace.titulo}
              </h3>
              <p className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-100`}>
                {pais?.nombre || 'País no especificado'}
              </p>
            </div>
            <div className="text-right">
              <div className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-100`}>
                {tipoCuenta?.nombre || 'Tipo no especificado'}
              </div>
              <div className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-200 font-semibold`}>
                {datosBancarios.nombreBanco}
              </div>
            </div>
          </div>

          {/* Información bancaria */}
          <div className="space-y-2">
            {/* Número de cuenta */}
            <div 
              className={`
                bg-white bg-opacity-20 backdrop-blur-sm rounded-lg cursor-pointer
                ${estiloMiniatura ? 'p-2' : 'p-3'}
                hover:bg-opacity-30 transition-all duration-200
              `}
              onClick={() => manejarCopia(datosBancarios.numeroCuenta, 'Número de cuenta')}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-100`}>
                    Número de cuenta
                  </p>
                  <p className={`${estiloMiniatura ? 'text-sm' : 'text-base'} font-mono font-bold`}>
                    {datosBancarios.numeroCuenta}
                  </p>
                </div>
                <i className={`fas fa-copy ${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-200`}></i>
              </div>
            </div>

            {/* CLABE/Código adicional */}
            {datosBancarios.clabe && (
              <div 
                className={`
                  bg-white bg-opacity-20 backdrop-blur-sm rounded-lg cursor-pointer
                  ${estiloMiniatura ? 'p-2' : 'p-3'}
                  hover:bg-opacity-30 transition-all duration-200
                `}
                onClick={() => manejarCopia(datosBancarios.clabe, pais?.campoAdicional || 'Código')}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-100`}>
                      {pais?.campoAdicional || 'Código adicional'}
                    </p>
                    <p className={`${estiloMiniatura ? 'text-sm' : 'text-base'} font-mono font-bold`}>
                      {datosBancarios.clabe}
                    </p>
                  </div>
                  <i className={`fas fa-copy ${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-200`}></i>
                </div>
              </div>
            )}

            {/* Información del titular */}
            {datosBancarios.nombreTitular && (
              <div className={`${estiloMiniatura ? 'mt-2' : 'mt-3'} pt-2 border-t border-blue-300 border-opacity-30`}>
                <div className="flex justify-between items-center">
                  <div>
                    <p className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-100`}>
                      Titular
                    </p>
                    <p className={`${estiloMiniatura ? 'text-sm' : 'text-base'} font-semibold`}>
                      {datosBancarios.nombreTitular}
                    </p>
                  </div>
                  {datosBancarios.identificacionTitular && (
                    <div className="text-right">
                      <p className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} text-blue-100`}>
                        ID
                      </p>
                      <p className={`${estiloMiniatura ? 'text-xs' : 'text-sm'} font-mono`}>
                        {datosBancarios.identificacionTitular}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Indicador de copiado */}
        {copiado && (
          <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold animate-pulse">
            ¡Copiado!
          </div>
        )}
      </div>
    </div>
  )
}

export default RenderizarCuentaBancaria 